import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {saveAs} from 'file-saver';
import {MatDialog} from '@angular/material/dialog';
import {MatStep} from '@angular/material/stepper';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, Observable, Observer, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, retry, take, takeUntil} from 'rxjs/operators';
import {KeycloakService} from 'keycloak-angular';
import {MatRadioChange} from '@angular/material/radio';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {DateUtil} from '../../../../common/util/DateUtil';
import {HelpDTO} from '../../../../other/models/HelpDTO';
import {ClmRequestBuilder} from '../../../services/clm-request-builder';
import {LookupValue} from '../../../types/lookup-value.enum';
import {CaseFuneralService} from '../../../services/case-funeral.service';
import {CaseFuneralDTO} from '../../../models/CaseFuneralDTO';
import {CaseType} from '../../../types';
import {ProviderDTO} from '../../../../ent/models/ProviderDTO';
import {LocationDTO} from '../../../models/LocationDTO';
import {PagedFilterDTO} from '../../../../common/models/PagedFilterDTO';
import {LocationService} from '../../../../ent/services/location.service';
import {ProviderService} from '../../../../ent/services/provider.service';
import {FileUploadService} from '../../../../common/services/file-upload.service';
import {GenericListDialogComponent} from '../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {CustomValidator} from '../../../../common';
import {ContactArrayDTO} from '../../../../ent/models/ContactsArrayDTO';
import {Common} from '../../../../common/util/Util';
import {FileUploadConfig} from '../../../../common/interface';
import {CaseIssuesStepComponent} from '../case-shared-steps/case-issues-step/case-issues-step.component';
import {CaseStatusStepComponent} from '../case-shared-steps/case-status-step/case-status-step.component';
import {CaseTransactionStepComponent} from '../case-shared-steps/case-transaction-step/case-transaction-step.component';
import {CaseAssociationStepComponent} from '../case-shared-steps/case-association-step/case-association-step.component';
import {SharedService} from '../../../../common/services/shared-service';
import {ProviderFacilityType} from '../../../types/provider-facility-type';
import {CasePaymentsStepComponent} from '../case-shared-steps/case-payments-step/case-payments-step.component';
import {HelpInfo} from '../../../types/help-info.enum';
import {CaseService} from '../../../services/case.service';
import {ContactDTO} from '../../../../ent/models/ContactDTO';
import {CaseDTO} from '../../../models/CaseDTO';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {DisplayStrings} from 'src/app/modules/common/constants/DisplayStrings';
import {FileDTO} from '../../../models/FileDTO';

enum ProviderDialogType {
  FuneralParlour = 'funeralParlour',
  RepatriationProvider = 'repatriationProvider',
  MedicalPractitioner = 'medicalPractitioner',
  Hospital = 'hospital'
}

// Detail Step

enum Controls {
  MedicalPractitionerOrTraditionalRadio = 'medicalPractitionerOrTraditional',
  TraditionalLeaderContactNumber = 'traditionalLeaderContactNumber',
}

enum DetailStepType {
  MedicalPractitioner = 'Medical Practitioner',
  TraditionalLeader = 'Traditional Leader'
}

enum DetailStepRadioGroupName {
  MedicalPractitionerTraditionalLeaderRadioGroup = 'medicalPractitionerTraditionalLeaderRadioGroup',
}

enum FormGroupName {
  AssociationFormGroup = 'associationFormGroup',
}

enum RoutePaths {
  Add = 'add-funeral-case',
  Edit = 'edit-funeral-case',
}

@Component({
  selector: 'app-add-case-funeral-view',
  templateUrl: './add-case-funeral-view.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss',
    './add-case-funeral-view.component.scss']
})
export class AddCaseFuneralViewComponent implements OnInit, OnDestroy {
  readonly controls = Controls;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly dateFormat = DateUtil.dateFormat;
  readonly caseType = CaseType;
  readonly providerDialogType = ProviderDialogType;
  readonly providerFacilityType = ProviderFacilityType;
  readonly routePaths = RoutePaths;
  readonly detailStepType = DetailStepType;
  readonly formGroupName = FormGroupName;

  heading: string;
  backPageName: string;
  helpInfo: HelpDTO[];
  isEditMode: boolean;

  caseId: number;
  caseFuneralDTO: CaseFuneralDTO;

  // ============================
  // Association Step
  associationFormGroup: FormGroup;

  // ============================
  // Detail Step
  selectedFuneralParlourProvider: ProviderDTO;
  selectedRepatriationProviderProvider: ProviderDTO;
  selectedMedicalPractitionerProvider: ProviderDTO;
  selectedHospitalProvider: ProviderDTO;
  selectedLocationOfDeath: LocationDTO;
  filteredProvidersByType: ProviderDTO[];  // Used by funeralParlour Or RepatriationProvider
  filteredProviders: ProviderDTO[];        // Used by hospital Or MedicalPractitioner
  filteredLocations: LocationDTO[];

  detailFormGroup: FormGroup;
  funeralParlourFormControl: FormControl;
  repatriationProviderFormControl: FormControl;
  medicalPractitionerFormControl: FormControl;
  hospitalFormControl: FormControl;
  dateOfDeathFormControl: FormControl;
  deathCertificateDateFormControl: FormControl;
  deathCertifierFormControl: FormControl;
  deathLocationFormControl: FormControl;
  dhaBarCodeFormControl: FormControl;
  communicationNumberFormControl: FormControl;
  traditionalLeaderNameFormControl: FormControl;
  traditionalLeaderContactNumberFormControl: FormControl;
  medicalPractitionerOrTraditionalLeaderFormControl: FormControl;
  privateToggleFormControl: FormControl;

  readonly detailStepRadioGroupName = DetailStepRadioGroupName;
  readonly locationOfDeathTextChanged$ = new Subject<string>();
  readonly funeralParlourOrRepatriationProviderTextChanged$ = new Subject<string>();
  readonly hospitalOrMedicalPractitionerTextChanged$ = new Subject<string>();
  // ============================
  // Documentation Step

  readonly TOTAL_REQUIRED_FILES_COUNTER: number = 9;

  // Note: Will be storing "fileId" not file content
  documentationFormGroup: FormGroup;
  funeralClaimFormFileFormControl: FormControl;
  dhaBi1663FormFileFormControl: FormControl;
  certifiedCopyOfDeathCertificateFileFormControl: FormControl;
  deceasedFileFormControl: FormControl;
  policyHolderIdentificationFileFormControl: FormControl;
  beneficiaryIdentificationFileFormControl: FormControl;
  beneficiaryBankStatementFileFileFormControl: FormControl;
  policeReportFormFileFormControl: FormControl;
  dha1680FileFormControl: FormControl;
  marriageCertificateFileFormControl: FormControl;
  letterOfAuthorityFileFormControl: FormControl;
  affidavitFileFileFormControl: FormControl;
  applicationFormFileFormControl: FormControl;

  // Date field
  documentationCompleteDateFormControl: FormControl;
  isDocumentationDateRequired = false;
  fileUploadConfig: FileUploadConfig[] = [];

  readonly length = 100;
  readonly pageSize = 5;
  readonly pageNumber = 0;
  readonly pageSizeOptions: number[] = [5, 10, 25, 100];

  transactionFormGroup: FormGroup;
  paymentFormGroup: FormGroup;
  issueTicketFormGroup: FormGroup;
  statusFormGroup: FormGroup;

  // ============================
  private loggedInUserName: string;

  // ============================

  @ViewChild('associationStep', {static: false}) private associationStep: CaseAssociationStepComponent;
  @ViewChild('detailStep', {static: false}) private detailStep: MatStep;
  @ViewChild('documentationStep', {static: false}) private documentationStep: MatStep;
  @ViewChild('transactionStep', {static: false}) private transactionStep: CaseTransactionStepComponent;
  @ViewChild('paymentStep', {static: false}) private paymentStep: CasePaymentsStepComponent;
  @ViewChild('issueStep', {static: false}) private issueStep: CaseIssuesStepComponent;
  @ViewChild('statusStep', {static: false}) private statusStep: CaseStatusStepComponent;

  // ViewChild for Mat Steps for a Stepper:
  @ViewChild('associationMatStep', {static: false}) private associationMatStep: MatStep;
  @ViewChild('paymentMatStep', {static: false}) private paymentMatStep: MatStep;
  @ViewChild('issuesMatStep', {static: false}) private issuesMatStep: MatStep;
  @ViewChild('transactionMatStep', {static: false}) private transactionMatStep: MatStep;
  @ViewChild('statusMatStep', {static: false}) private statusMatStep: MatStep;

  // Set payloads
  private associationPayload: any;
  private issueTicketsPayload: any;
  private transactionPayload: any;
  private statusPayload: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private caseService: CaseService,
    private caseFuneralService: CaseFuneralService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private fileUploadService: FileUploadService,
    private keycloakService: KeycloakService,
    private locationService: LocationService,
    private providerService: ProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private userAuthorisationService: UserAuthorisationService) {
    this.heading = 'Add Case';
    this.backPageName = 'claim-management';
    this.isEditMode = false;
  }

  get isMedicalPractitioner(): boolean {
    return this.detailFormGroup.get(Controls.MedicalPractitionerOrTraditionalRadio).value === DetailStepType.MedicalPractitioner;
  }

  get isTraditionalLeader(): boolean {
    return this.detailFormGroup.get(Controls.MedicalPractitionerOrTraditionalRadio).value === DetailStepType.TraditionalLeader;
  }

  get communicationNumberAbstractControl() {
    return this.detailFormGroup.get('communicationNumber');
  }

  get isAuthorised(): boolean {
    return this.userAuthorisationService.hasRoleClaimsAssessor();
  }

  get bannerText(): string {
    return DisplayStrings.permissionsErrorMessages.cannotEditCase;
  }

  ngOnInit(): void {
    this.initSubscriptions();
    if (this.route.snapshot.url[1].path === this.routePaths.Edit) {
      this.getCaseFuneralByCaseId(this.caseId);
      this.heading = 'Edit Case';
      this.isEditMode = true;
    }
    else if (this.route.snapshot.url[1].path === this.routePaths.Add) {
      this.heading = 'Add Case';
      this.isEditMode = false;
    }

    this.initAssociationStepFormControls();
    this.initDetailStepFormControls();
    this.initDocumentationStepFormControls();
    this.initTransactionStepFormControls();
    this.initPaymentStepFormControls();
    this.initIssuesStepForm();
    this.initStatusStepFormControls();
    this.getHelpInfo();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {
    // Route Subscription
    this.route.params.subscribe(params => {
      this.caseId = params.id;
    });

    this.locationOfDeathTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((cityName: string) => {
          this.getListOfLocations(cityName);
        },
        error => console.log(error));


    this.funeralParlourOrRepatriationProviderTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((value: string) => {
          this.filteredProvidersByType = [];
          this.getFilteredListOfFuneralProviders(value);
        },
        error => console.log(error));

    this.hospitalOrMedicalPractitionerTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((value: string) => {
          this.getFilteredListOfProviders(value);
        },
        error => console.log(error));
    // ----------------------------------------

    this.keycloakService
      .loadUserProfile()
      .then(profile => {
        this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;
      });

  }

  initAssociationStepFormControls(): void {
    this.associationFormGroup = this.formBuilder.group({});
  }

  initDetailStepFormControls(): void {
    this.funeralParlourFormControl = new FormControl(null, [Validators.required]);
    this.dateOfDeathFormControl = new FormControl(null, [Validators.required]);
    this.deathCertificateDateFormControl = new FormControl(null);
    this.deathCertifierFormControl = new FormControl(null);
    this.deathLocationFormControl = new FormControl(null, [Validators.required]);
    this.repatriationProviderFormControl = new FormControl();
    this.medicalPractitionerFormControl = new FormControl();
    this.dhaBarCodeFormControl = new FormControl(null, [Validators.required]);
    this.communicationNumberFormControl = new FormControl(null, [Validators.required, CustomValidator.mobileNumberValidator]);

    // Note: Add/Clear validators when radio option updates
    this.traditionalLeaderNameFormControl = new FormControl();
    this.traditionalLeaderContactNumberFormControl = new FormControl();

    this.hospitalFormControl = new FormControl();
    this.medicalPractitionerOrTraditionalLeaderFormControl = new FormControl(this.detailStepType.MedicalPractitioner);
    this.privateToggleFormControl = new FormControl(false);

    this.detailFormGroup = this.formBuilder.group({
      dhaBarCode: this.dhaBarCodeFormControl,
      dateOfDeath: this.dateOfDeathFormControl,
      traditionalLeaderName: this.traditionalLeaderNameFormControl,
      traditionalLeaderContactNumber: this.traditionalLeaderContactNumberFormControl,
      funeralParlour: this.funeralParlourFormControl,
      repatriationProvider: this.repatriationProviderFormControl,
      medicalPractitioner: this.medicalPractitionerFormControl,
      hospital: this.hospitalFormControl,
      deathLocation: this.deathLocationFormControl,
      medicalPractitionerOrTraditional: this.medicalPractitionerOrTraditionalLeaderFormControl,
      private: this.privateToggleFormControl,
      communicationNumber: this.communicationNumberFormControl
    });

    // Add Case set validators on "Medical Practitioner" default value:
    this.handleDetailStepRadioValueChangedValidators(this.medicalPractitionerOrTraditionalLeaderFormControl.value);
  }

  initDocumentationStepFormControls(): void {
    this.funeralClaimFormFileFormControl = new FormControl();
    this.dhaBi1663FormFileFormControl = new FormControl();
    this.certifiedCopyOfDeathCertificateFileFormControl = new FormControl();
    this.deceasedFileFormControl = new FormControl();
    this.policyHolderIdentificationFileFormControl = new FormControl();
    this.beneficiaryIdentificationFileFormControl = new FormControl();
    this.beneficiaryBankStatementFileFileFormControl = new FormControl();
    this.policeReportFormFileFormControl = new FormControl();
    this.dha1680FileFormControl = new FormControl();
    this.marriageCertificateFileFormControl = new FormControl();
    this.letterOfAuthorityFileFormControl = new FormControl();
    this.affidavitFileFileFormControl = new FormControl();
    this.applicationFormFileFormControl = new FormControl();
    this.documentationCompleteDateFormControl = new FormControl();

    this.documentationFormGroup = this.formBuilder.group({
      funeralClaimFormFile: this.funeralClaimFormFileFormControl,
      dhaBi1663FormFile: this.dhaBi1663FormFileFormControl,
      certifiedCopyOfDeathCertificateFile: this.certifiedCopyOfDeathCertificateFileFormControl,
      deceasedFile: this.deceasedFileFormControl,
      policyHolderIdentificationFile: this.policyHolderIdentificationFileFormControl,
      beneficiaryIdentificationFile: this.beneficiaryIdentificationFileFormControl,
      beneficiaryBankStatementFile: this.beneficiaryBankStatementFileFileFormControl,
      policeReportFormFile: this.policeReportFormFileFormControl,
      dha1680File: this.dha1680FileFormControl,
      marriageCertificateFile: this.marriageCertificateFileFormControl,
      letterOfAuthorityFile: this.letterOfAuthorityFileFormControl,
      affidavitFile: this.affidavitFileFileFormControl,
      applicationFormFile: this.applicationFormFileFormControl,
      documentationCompleteDate: this.documentationCompleteDateFormControl
    });

    this.fileUploadConfig = [
      {
        label: 'Funeral Claim Form',
        name: 'funeralClaimFormFileConfig',
        isRequired: true,
        formControlName: 'funeralClaimFormFile',
        formControlInstance: this.funeralClaimFormFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Department of Home Affairs BI 1663 Form',
        name: 'dhaBi1663FormFileConfig',
        isRequired: true,
        formControlName: 'dhaBi1663FormFile',
        formControlInstance: this.dhaBi1663FormFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Certified copy of Death Certificate',
        name: 'certifiedCopyOfDeathCertificateFileConfig',
        isRequired: true,
        formControlName: 'certifiedCopyOfDeathCertificateFile',
        formControlInstance: this.certifiedCopyOfDeathCertificateFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Certified copy of Deceased ID',
        name: 'deceasedFileConfig',
        isRequired: true,
        formControlName: 'deceasedFile',
        formControlInstance: this.deceasedFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Certified copy of Policy Holder’s ID',
        name: 'policyHolderIdentificationConfig',
        isRequired: true,
        formControlName: 'policyHolderIdentificationFile',
        formControlInstance: this.policyHolderIdentificationFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Certified copy of Beneficiary’s ID',
        name: 'beneficiaryIdentificationConfig',
        isRequired: true,
        formControlName: 'beneficiaryIdentificationFile',
        formControlInstance: this.beneficiaryIdentificationFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Certified copy of Beneficiary’s Bank Statement',
        name: 'beneficiaryBankStatementConfig',
        isRequired: true,
        formControlName: 'beneficiaryBankStatementFile',
        formControlInstance: this.beneficiaryBankStatementFileFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Police Report',
        name: 'policeReportFormConfig',
        isRequired: true,
        formControlName: 'policeReportFormFile',
        formControlInstance: this.policeReportFormFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Department of Home Affairs 1680 Form',
        name: 'dha1680FileConfig',
        isRequired: true,
        formControlName: 'dha1680File',
        formControlInstance: this.dha1680FileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Marriage Certificate',
        name: 'marriageCertificateConfig',
        isRequired: false,
        formControlName: 'marriageCertificateFile',
        formControlInstance: this.marriageCertificateFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Letter of Authority',
        name: 'letterOfAuthorityConfig',
        isRequired: false,
        formControlName: 'letterOfAuthorityFile',
        formControlInstance: this.letterOfAuthorityFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'South African ID Affadavit',
        name: 'affidavitFileConfig',
        isRequired: false,
        formControlName: 'affidavitFile',
        formControlInstance: this.affidavitFileFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      },
      {
        label: 'Application Form',
        name: 'applicationFormFileConfig',
        isRequired: false,
        formControlName: 'applicationFormFile',
        formControlInstance: this.applicationFormFileFormControl,
        uploadedFileData: null,
        clearFileData: true
      }
    ];
  }

  initTransactionStepFormControls(): void {
    this.transactionFormGroup = this.formBuilder.group({});
  }

  initPaymentStepFormControls(): void {
    this.paymentFormGroup = this.formBuilder.group({});
  }

  initIssuesStepForm(): void {
    this.issueTicketFormGroup = this.formBuilder.group({});
  }

  initStatusStepFormControls(): void {
    this.statusFormGroup = this.formBuilder.group({});
  }

  getHelpInfo(): void {
    this.sharedService.getHelpInfo(HelpInfo.FuneralCase);
    this.sharedService.getHelpInfo(HelpInfo.GeneralCase);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onUpdateHeadingChanged(value: string): void {
    this.heading = value;
    this.cdr.detectChanges();
  }

  onUpdateStepForm($event: any, formGroupName: FormGroupName): void {
    if (this.formGroupName.AssociationFormGroup === formGroupName) {
      this.associationFormGroup = $event;
    }
    this.cdr.detectChanges();
  }

  onLocationOfDeathSelected(locationName: any): void {
    if (this.filteredLocations && this.filteredLocations.length > 0) {
      this.selectedLocationOfDeath = this.filteredLocations.find((item: LocationDTO) => locationName === this.getLocationDisplayString(item));
    }
  }

  onBlurLocationOfDeath(): void {
    if (
      this.filteredLocations &&
      this.filteredLocations.length > 0
    ) {
      const value = this.deathLocationFormControl.value;
      const isMatched = this.filteredLocations.find((item: LocationDTO) => value === this.getLocationDisplayString(item));
      if (!isMatched) {
        this.clearLocationOfDeath();
      }
    }
    else {
      this.clearLocationOfDeath();
    }
  }

  onSelectionChange(provider: ProviderDTO, type: ProviderDialogType): void {
    switch (type) {
      case ProviderDialogType.FuneralParlour:
        this.selectedFuneralParlourProvider = provider;
        break;
      case ProviderDialogType.RepatriationProvider:
        this.selectedRepatriationProviderProvider = provider;
        break;
      case ProviderDialogType.Hospital:
        this.selectedHospitalProvider = provider;
        break;
      case ProviderDialogType.MedicalPractitioner:
        this.selectedMedicalPractitionerProvider = provider;
        break;
    }
  }

  onBlurFuneralParlourOrRepatriationProviderSelected(type: ProviderDialogType): void {
    if (
      this.filteredProvidersByType &&
      this.filteredProvidersByType.length > 0
    ) {
      let value;
      if (type === this.providerDialogType.FuneralParlour) {
        value = this.funeralParlourFormControl.value;
      }
      else if (type === this.providerDialogType.RepatriationProvider) {
        value = this.repatriationProviderFormControl.value;
      }
      const isMatched = this.filteredProvidersByType.find((item: ProviderDTO) => value === this.getProviderDisplayName(item));

      if (!isMatched) {
        if (type === this.providerDialogType.FuneralParlour) {
          this.clearFuneralParlourProvider();
        }
        else if (type === this.providerDialogType.RepatriationProvider) {
          this.clearRepatriationProvider();
        }
      }
    }
    else {
      if (type === this.providerDialogType.FuneralParlour) {
        this.clearFuneralParlourProvider();
      }
      else if (type === this.providerDialogType.RepatriationProvider) {
        this.clearRepatriationProvider();
      }
    }
  }

  onBlurHospitalOrMedicalPractitionerProviderSelected(type: ProviderDialogType): void {
    if (
      this.filteredProviders &&
      this.filteredProviders.length > 0
    ) {
      let value;
      if (type === this.providerDialogType.Hospital) {
        value = this.hospitalFormControl.value;
      }
      else if (type === this.providerDialogType.MedicalPractitioner) {
        value = this.medicalPractitionerFormControl.value;
      }
      const isMatched = this.filteredProviders.find((item: ProviderDTO) => value === this.getProviderDisplayName(item));

      if (!isMatched) {
        if (type === this.providerDialogType.Hospital) {
          this.clearHospitalProvider();
        }
        else if (type === this.providerDialogType.MedicalPractitioner) {
          this.clearMedicalPractitionerProvider();
        }
      }
    }
    else {
      if (type === this.providerDialogType.Hospital) {
        this.clearHospitalProvider();
      }
      else if (type === this.providerDialogType.MedicalPractitioner) {
        this.clearMedicalPractitionerProvider();
      }
    }
  }

  resetDetailFormGroup(): void {
    this.clearFuneralParlourProvider();
    this.clearRepatriationProvider();
    this.clearMedicalPractitionerProvider();
    this.clearHospitalProvider();
    this.clearFilteredProviders();
    this.clearLocationOfDeath();
    this.clearFilteredLocations();
    this.detailFormGroup.reset();
  }

  clearFuneralParlourProvider(): void {
    this.funeralParlourFormControl.reset();
    this.selectedFuneralParlourProvider = null;
  }

  clearRepatriationProvider(): void {
    this.repatriationProviderFormControl.reset();
    this.selectedRepatriationProviderProvider = null;
  }

  clearMedicalPractitionerProvider(): void {
    this.medicalPractitionerFormControl.reset();
    this.selectedMedicalPractitionerProvider = null;
  }

  clearHospitalProvider(): void {
    this.hospitalFormControl.reset();
    this.selectedHospitalProvider = null;
  }

  clearLocationOfDeath(): void {
    this.deathLocationFormControl.reset();
    this.selectedLocationOfDeath = null;
  }

  clearFilteredProviders(): void {
    this.filteredProviders = [];
  }

  clearFilteredLocations(): void {
    this.filteredLocations = [];
  }

  getFilteredListOfProviders(value: string): void {
    const byNamePagedRequestDTO =
      ClmRequestBuilder.getTblProvidersPagedRequestDTO({
        filterField: 'name',
        filterValue: value
      });

    const byBusinessLicenseNumberPagedRequestDTO =
      ClmRequestBuilder.getTblProvidersPagedRequestDTO({
        filterField: 'businessLicenseNumber',
        filterValue: value
      });

    forkJoin(
      this.providerService.getListOfProviders(byNamePagedRequestDTO),
      this.providerService.getListOfProviders(byBusinessLicenseNumberPagedRequestDTO)
    )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((
          responses: [
            PagedResponseDTO<ProviderDTO>,
            PagedResponseDTO<ProviderDTO>
          ]) => {
          this.filteredProviders = responses[0].resultList.concat(
            responses[1].resultList
          );
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  getFilteredListOfFuneralProviders(value: string): void {

    const byNamePagedRequestDTO =
      ClmRequestBuilder.getTblProvidersPagedRequestDTO({
        filterField: 'name',
        filterValue: value
      });

    // Add Additional Attributes on Field Mapping to filter data:
    const pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = 'providerFacilityTypeByProviderFacilityTypeId.providerFacilityTypeId';
    pagedFilterDTO.value = `${this.providerFacilityType.FUNERAL_PARLOUR},${this.providerFacilityType.REPATRIATION_AGENT}`;
    pagedFilterDTO.operator = 'in';
    byNamePagedRequestDTO.fieldMap['providerFacilityTypeByProviderFacilityTypeId'] = pagedFilterDTO;

    const byBusinessLicenseNumberPagedRequestDTO =
      ClmRequestBuilder.getTblProvidersPagedRequestDTO({
        filterField: 'businessLicenseNumber',
        filterValue: value
      });

    forkJoin(
      this.providerService.getListOfProviders(byNamePagedRequestDTO),
      this.providerService.getListOfProviders(byBusinessLicenseNumberPagedRequestDTO)
    )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((
          responses: [
            PagedResponseDTO<ProviderDTO>,
            PagedResponseDTO<ProviderDTO>
          ]) => {
          this.filteredProvidersByType = responses[0].resultList.concat(responses[1].resultList);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  showSelectedProviderDialog(type: ProviderDialogType): void {
    const COLUMNS_NAMES = 'Licence Number;Contact Number;Facility Type';
    const columns: string[] = [COLUMNS_NAMES];
    let heading;
    let selectedProviderBasedOnType;

    if (type === this.providerDialogType.FuneralParlour && this.selectedFuneralParlourProvider) {
      selectedProviderBasedOnType = this.selectedFuneralParlourProvider;
    }
    else if (type === this.providerDialogType.RepatriationProvider && this.selectedRepatriationProviderProvider) {
      selectedProviderBasedOnType = this.selectedRepatriationProviderProvider;
    }
    else if (type === this.providerDialogType.MedicalPractitioner && this.selectedMedicalPractitionerProvider) {
      selectedProviderBasedOnType = this.selectedMedicalPractitionerProvider;
    }
    else if (type === this.providerDialogType.Hospital && this.selectedHospitalProvider) {
      selectedProviderBasedOnType = this.selectedHospitalProvider;
    }
    if (selectedProviderBasedOnType) {
      this.getProviderById(selectedProviderBasedOnType.providerId)
        .pipe(
          take(1)
        )
        .subscribe((providerDetails: ProviderDTO) => {
          heading = providerDetails.name;
          columns.push(this.createDisplayStringGenericListDialog(providerDetails));

          this.dialog.open(GenericListDialogComponent, {
            width: '40vw',
            data: {list: columns, heading}
          });
        });
    }
    else {
      const snackBarMapping = {
        [this.providerDialogType.FuneralParlour]: 'Funeral Parlour',
        [this.providerDialogType.RepatriationProvider]: 'Repatriation Service Provider',
        [this.providerDialogType.MedicalPractitioner]: 'Medical Practitioner',
        [this.providerDialogType.Hospital]: 'Hospital'
      };
      this.sharedService.openSnackBar(`No ${snackBarMapping[type]} is selected`, 'Dismiss');
      return;
    }
  }

  onFileUploadSuccess(uploadedFileResponse: any): void {
    if (!uploadedFileResponse) {
      return;
    }
    const {configName, fileId} = uploadedFileResponse;
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);
    if (!config) {
      this.sharedService.openSnackBar('No matching file upload config found', 'Dismiss');
      return;
    }

    const previousUploadedFile = config.uploadedFileData;

    // Delete Existing Uploaded File
    // - C1: File already uploaded but modified on edit case (Edit Case)
    if (previousUploadedFile && previousUploadedFile.file) {
      const {fileId, name, extension} = previousUploadedFile;

      this.fileUploadService.deleteFile(fileId)
        .pipe(
          take(1)
        )
        .subscribe(
          () => {
            const {formControlName} = config;
            this.sharedService.openSnackBar(`old file ${name}${extension} deleted successfully!`, 'Dismiss');
            this.clearUploadedFileData(configName); // since file is deleted from server
            this.clearDocumentationFormGroupControlValue(formControlName); // clear form control value

            // Note: Update fileId with new file uploaded
            this.documentationFormGroup.patchValue({
              [formControlName]: fileId
            });
            // Reassign new uploaded file:
            config.uploadedFileData = uploadedFileResponse;
          },
          (errorResponse) => {
            const error = errorResponse.error;
            console.log('Read -> Reason', error);
          }
        );
    }
    else if (uploadedFileResponse && uploadedFileResponse.file) {
      const {formControlName} = config;
      // - C1:upload new file (Add Case)
      // - C2: upload file on edit case if no file was uploaded (Edit Case)

      // Note: Store uploaded fileId that will be required in payload at end of the step
      this.documentationFormGroup.patchValue({
        [formControlName]: fileId
      });
      config.uploadedFileData = uploadedFileResponse;
    }

    this.isDocumentationCompletionDateRequired();

    if (this.isEditMode) {
      // AdHoc requirement to upload document on Edit Case
      // In Case Funeral (Complete Payload is Submitted)
      this.handleAddNewDocumentWithoutCaseSave();
    }
  }

  handleAddNewDocumentWithoutCaseSave(): void {
    // ----------------------------
    // Important: Get Updated Payload from each step BEFORE creating Final Payload
    this.setPayloadFromEachStep();
    // ----------------------------

    const caseFuneralDTOPayload: CaseFuneralDTO = this.getCaseFuneralDTOPayload();
    this.caseFuneralService.saveCaseFuneralDocument(caseFuneralDTOPayload)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: CaseFuneralDTO) => {
          this.setDocumentationFormForEdit(response);
        },
        error: (_) => {
          this.sharedService.openSnackBar(`Failed to upload file`, 'Dismiss');
        }
      });
  }

  deleteFile(fileId: number, configName: string): void {
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);
    if (!config) {
      this.sharedService.openSnackBar('No matching file upload config found', 'Dismiss');
      return;
    }
    const {name, extension} = config.uploadedFileData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete file ${name}${extension}?`
    });

    dialogRef.afterClosed()
      .pipe(
        take(1)
      )
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.fileUploadService.deleteFile(fileId)
            .pipe(
              take(1),
              finalize(() => {
                // Note: In Case of Success or Error both case local state needs to be cleared
                if (config.uploadedFileData) {
                  const {formControlName} = config;
                  this.clearUploadedFileData(configName); // clear local state for previous uploaded file
                  this.clearDocumentationFormGroupControlValue(formControlName); // clear formControl value
                  this.sharedService.openSnackBar(`File with ${name}${extension} deleted successfully!`, 'Dismiss');
                  this.isDocumentationCompletionDateRequired(); // clear required validator

                  if (this.isEditMode) {
                    // Once file deleted save Updated payload:
                    this.handleAddNewDocumentWithoutCaseSave();
                  }
                }
              })
            )
            .subscribe(
              () => {
                //  EMPTY
              },
              (errorResponse) => {
                const error = errorResponse.error;
                console.log(`File with ${name}${extension} deleted from Angular local state not from server!`);
                console.log('See Note: ', error);
                /*
                  Reason:
                  Discussed with jason it doesn't delete, just catch the error and do nothing With it.
                  Reasons: since file associated with ids 1,2...xyz can't deleted since those
                  keys are used as foreign keys in table therefore ignore handleError case for file
                  deletion.
                */
              }
            );
        }
        else {
          dialogRef.close();
        }
      });
  }

  downloadFile($event, configName): void {
    $event.preventDefault();
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);
    if (!config) {
      this.sharedService.openSnackBar('No matching file upload config found', 'Dismiss');
      return;
    }

    this.fileUploadService.getFile(config?.uploadedFileData?.fileId)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (fileResponse: FileDTO) => {
          const {name, extension, file} = fileResponse;
          const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
          const blob = new Blob([blobData]);
          saveAs(blob, `${name}${extension}`);
        },
        error: (error) => {
          console.log('Failed to fetch file');
        }
      });
  }

  resetStatusFormGroup(): void {
    this.statusFormGroup.reset();

    // Note: reset of form resets value and "yes" default value is not selected therefore explicitly reset to init values:
    this.initStatusStepFormControls();
  }

  resetIssueTicketFormGroup(): void {
    this.issueTicketFormGroup.reset();
  }

  saveCaseFuneralDTO(): void {
    // ----------------------------
    // Important: Get Updated Payload from each step BEFORE creating Final Payload
    this.setPayloadFromEachStep();
    // ----------------------------

    const caseFuneralDTOPayload: CaseFuneralDTO = this.getCaseFuneralDTOPayload();
    this.disableActivityPage();
    this.caseFuneralService.saveCaseFuneral(caseFuneralDTOPayload)
      .pipe(takeUntil(this.destroyed$),)
      .subscribe({
        next: (response: CaseFuneralDTO | any) => {
          if (response) {
            this.sharedService.openSnackBar(`Case ${this.isEditMode ? 'edited' : 'added'} successfully`, 'Dismiss');
            this.resetState();
            this.router.navigate(['/claim-management'], {queryParams: {tab: 'cases'}});
          }
          else if (response && response.businessMessage) {
            this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
          }
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  onDetailStepRadioValueChanged($event: MatRadioChange): void {
    // Note: Wrapper method  `_handleDetailStepRadioValueChangedValidators` method will be used:
    // Add Case: -> $event: MatRadioChange
    // Edit Case -> string value
    this.handleDetailStepRadioValueChangedValidators($event.source.value);
  }

  onDetailStepToggleValueChanged($event: MatSlideToggleChange): void {
    // Note: Wrapper method  `_handleDetailStepToggleValueChangedValidators` method will be used:
    // Add Case: -> $event: MatSlideToggleChange
    // Edit Case -> string boolean
    this.handleDetailStepToggleValueChangedValidators($event.source.checked);
  }

  getLocationDisplayString(location: LocationDTO): string {
    return `${location.cityTown}, ${location.country?.name}`;
  }

  setPayloadFromEachStep(): void {
    this.associationPayload = this.associationStep.getPayload();
    this.issueTicketsPayload = this.issueStep.getPayload();
    this.statusPayload = this.statusStep.getPayload();

    // Step is visible on "Edit Mode" only
    if (this.isEditMode) {
      this.transactionPayload = this.transactionStep.getPayload();
    }
  }

  // Detail Step:
  getProviderDisplayName(provider: ProviderDTO): string {
    if (provider) {
      return `${provider.businessLicenseNumber}: ${provider.name}`;
    }
    return null;
  }

  handleCommunicationNumberValue(personId: number): void {
    if (personId) {
      this.caseService.getContactByInsuredPerson(personId)
        .pipe(
          take(1),
        )
        .subscribe((response: ContactDTO) => {
            if (response) {
              if (!this.isEditMode) {
                this.communicationNumberAbstractControl.patchValue(response.mobileNumber);
              }
              else {
                const caseDTO: CaseDTO = this.caseFuneralDTO.caseDTO;
                if (!caseDTO.communicationNumber) {
                  this.communicationNumberAbstractControl.patchValue(response.mobileNumber);
                }
                else {
                  this.communicationNumberAbstractControl.patchValue(caseDTO.communicationNumber);
                }
              }
            }
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          });
    }
  }

  private handleDetailStepToggleValueChangedValidators(value: boolean): void {
    const funeralParlour = 'funeralParlour';
    if (value) {
      this.clearValidatorOnFormControlInDetailStep(funeralParlour);
      this.clearFuneralParlourProvider();
    }
    else {
      this.setValidatorOnFormControlInDetailStep(funeralParlour, [Validators.required]);
    }
  }

  private handleDetailStepRadioValueChangedValidators(value: string): void {
    // Note: Name are same what defined in formGroup:
    const traditionalLeaderName = 'traditionalLeaderName';
    const traditionalLeaderContactNumber = 'traditionalLeaderContactNumber';
    const medicalPractitioner = 'medicalPractitioner';

    if (value === this.detailStepType.MedicalPractitioner) {
      // Clear Traditional hidden fields
      this.clearValidatorOnFormControlInDetailStep(traditionalLeaderName);
      this.clearValidatorOnFormControlInDetailStep(traditionalLeaderContactNumber);
      this.setValidatorOnFormControlInDetailStep(medicalPractitioner, [Validators.required]);
    }
    else if (value === this.detailStepType.TraditionalLeader) {
      // Clear Medical hidden field
      this.clearMedicalPractitionerProvider();
      this.clearValidatorOnFormControlInDetailStep(medicalPractitioner);
      this.setValidatorOnFormControlInDetailStep(traditionalLeaderName, [Validators.required]);
      this.setValidatorOnFormControlInDetailStep(traditionalLeaderContactNumber, [
        Validators.minLength(10),
        Validators.maxLength(10),
        CustomValidator.numeric
      ]);
    }
  }

  private setValidatorOnFormControlInDetailStep(controlName: string, validators: ((control: AbstractControl) => (ValidationErrors | null))[]) {
    this.detailFormGroup.get(controlName).setValidators(validators);
    this.detailFormGroup.get(controlName).updateValueAndValidity();
  }

  private clearValidatorOnFormControlInDetailStep(controlName: string): void {
    this.detailFormGroup.get(controlName).clearValidators();
    this.detailFormGroup.get(controlName).updateValueAndValidity();
    this.detailFormGroup.get(controlName).reset();
  }

  private isDocumentationCompletionDateRequired(): void {
    let requiredFilesCounter = 0;
    Object.keys(this.documentationFormGroup.controls)
      .forEach((controlName: string) => {
        if ([
          'funeralClaimFormFile',
          'dhaBi1663FormFile',
          'certifiedCopyOfDeathCertificateFile',
          'deceasedFile',
          'policyHolderIdentificationFile',
          'beneficiaryIdentificationFile',
          'beneficiaryBankStatementFile',
          'policeReportFormFile',
          'dha1680File'].includes(controlName)
        ) {
          if (!!this.documentationFormGroup.get(controlName).value) {
            requiredFilesCounter++;
          }
        }
      });

    if (requiredFilesCounter === this.TOTAL_REQUIRED_FILES_COUNTER) {
      this.documentationFormGroup.get('documentationCompleteDate').setValidators([Validators.required]);
      this.documentationFormGroup.get('documentationCompleteDate').updateValueAndValidity();
      this.isDocumentationDateRequired = true;
    }
    else {
      // Case when required file is deleted make sure to clear validator on "Documentation Completion Date"
      this.documentationFormGroup.get('documentationCompleteDate').clearValidators();
      this.documentationFormGroup.get('documentationCompleteDate').updateValueAndValidity();
      this.isDocumentationDateRequired = false;
    }
  }

  private disableActivityPage(): void {
    this.associationMatStep.editable = false;
    this.detailStep.editable = false;
    this.documentationStep.editable = false;
    if (this.isEditMode) {
      this.transactionMatStep.editable = false;
      this.paymentMatStep.editable = false;
    }
    this.issuesMatStep.editable = false;
    this.statusMatStep.editable = false;
  }

  private enableActivityPage(): void {
    this.associationMatStep.editable = true;
    this.detailStep.editable = true;
    this.documentationStep.editable = true;
    if (this.isEditMode) {
      this.transactionMatStep.editable = true;
      this.paymentMatStep.editable = true;
    }
    this.issuesMatStep.editable = true;
    this.statusMatStep.editable = true;
  }

  private clearUploadedFileData(configName: string): void {
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);
    if (!config) {
      this.sharedService.openSnackBar('No matching file upload config found', 'Dismiss');
      return;
    }
    config.uploadedFileData = null;
  }

  private resetDocumentationFormGroup(): void {
    this.clearAllUploadedFileData();
    this.documentationFormGroup.reset();
  }

  private clearAllUploadedFileData(): void {
    this.fileUploadConfig.forEach((fileConfig: FileUploadConfig) => {
      const config: FileUploadConfig = this.getFileUploadConfigBy(fileConfig.name);
      config.uploadedFileData = null;
    });
  }

  private resetTransactionFormGroup(): void {
    this.transactionFormGroup.reset();
  }

  private clearDocumentationFormGroupControlValue(formControlName: string): void {
    this.documentationFormGroup.get(formControlName).setValue(null);
  }

  private createDisplayStringGenericListDialog(provider: ProviderDTO): string {
    let contactsList: ContactArrayDTO[] = [];

    if (provider.contacts && provider.contacts.length > 0) {
      contactsList = this.sortByColumn(provider.contacts, 'startDate');
    }
    return `${provider.businessLicenseNumber};
            ${contactsList && contactsList.length > 0 ? contactsList[0].contact?.mobileNumber : 'No details available'};
            ${provider.providerFacilityType ? provider.providerFacilityType.name : 'No details available'}`;
  }

  private getProviderById(providerId: number): Observable<ProviderDTO> {
    return new Observable((observer: Observer<any>) => {

      this.providerService.getProvider(providerId)
        .pipe(
          take(1),
        )
        .subscribe(
          (response: ProviderDTO) => {
            observer.next(response);
          },
          (errorResponse) => {
            observer.next(false);
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
    });
  }

  private getListOfLocations(cityName: string): void {
    const pagedRequestDTO = ClmRequestBuilder.getListOfLocationPagedRequestDTO(cityName);
    this.locationService.getListOfLocations(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response) => {
          this.filteredLocations = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getCaseFuneralByCaseId(caseId: number): void {
    this.caseFuneralService.getCaseFuneralByCaseId(caseId)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: CaseFuneralDTO) => {
          if (response) {
            this.caseFuneralDTO = response;
            this.setFormsForEdit(response);
          }
          else {
            this.sharedService.openSnackBar(`Case ${caseId} data not found`, 'Dismiss');
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private setFormsForEdit(data: CaseFuneralDTO): void {
    this.setDetailFormForEdit(data);
    this.setDocumentationFormForEdit(data);
  }

  private setDetailFormForEdit(data: CaseFuneralDTO): void {
    const {
      funeralParlour,
      repatriationProvider,
      medicalPractitioner,
      hospital,
      dateOfDeath,
      deathCertificateDate,
      deathCertifier,
      dhaBarCode,
      deathLocation,
      traditionalLeaderName,
      traditionalLeaderContactNumber,
      caseDTO
    } = data;

    if (funeralParlour != null) {
      const displayName = this.getProviderDisplayName(funeralParlour);
      this.funeralParlourFormControl.setValue(displayName);
    }
    if (repatriationProvider != null) {
      const displayName = this.getProviderDisplayName(repatriationProvider);
      this.repatriationProviderFormControl.setValue(displayName);
    }
    if (medicalPractitioner != null) {
      const displayName = this.getProviderDisplayName(medicalPractitioner);
      this.medicalPractitionerFormControl.setValue(displayName);
    }
    if (hospital != null) {
      const displayName = this.getProviderDisplayName(hospital);
      this.hospitalFormControl.setValue(displayName);
    }

    if (deathCertificateDate) {
      this.deathCertificateDateFormControl.setValue(new Date(deathCertificateDate));
    }
    else {
      this.deathCertificateDateFormControl.setValue(null);
    }

    this.dateOfDeathFormControl.setValue(new Date(dateOfDeath));
    this.deathCertifierFormControl.setValue(deathCertifier);
    this.dhaBarCodeFormControl.setValue(dhaBarCode);
    this.deathLocationFormControl.setValue(this.getLocationDisplayString(deathLocation));
    this.traditionalLeaderNameFormControl.setValue(traditionalLeaderName);
    this.traditionalLeaderContactNumberFormControl.setValue(traditionalLeaderContactNumber);
    this.communicationNumberFormControl.setValue(caseDTO.communicationNumber);
    this.selectedFuneralParlourProvider = funeralParlour;
    this.selectedRepatriationProviderProvider = repatriationProvider;
    this.selectedMedicalPractitionerProvider = medicalPractitioner;
    this.selectedHospitalProvider = hospital;
    this.selectedLocationOfDeath = deathLocation;

    // Note: Select Toggle state option based "Funeral Parlour" value
    if (!this.funeralParlourFormControl.value) {
      this.privateToggleFormControl.setValue(true);
      this.handleDetailStepToggleValueChangedValidators(true);
    }

    // Note: Select Radio btn option based "Medical Practitioner" or "Traditional Leader" value:
    if (this.medicalPractitionerFormControl.value) {
      this.medicalPractitionerOrTraditionalLeaderFormControl.setValue(this.detailStepType.MedicalPractitioner);
      this.handleDetailStepRadioValueChangedValidators(this.detailStepType.MedicalPractitioner);

    }
    else if (this.traditionalLeaderNameFormControl.value && this.traditionalLeaderContactNumberFormControl.value) {
      this.medicalPractitionerOrTraditionalLeaderFormControl.setValue(this.detailStepType.TraditionalLeader);
      this.handleDetailStepRadioValueChangedValidators(this.detailStepType.TraditionalLeader);
    }
  }

  private setDocumentationFormForEdit(data: CaseFuneralDTO): void {
    const {
      funeralClaimFormFile,
      dhaBi1663FormFile,
      certifiedCopyOfDeathCertificateFile,
      deceasedFile,
      policyHolderIdentificationFile,
      beneficiaryIdentificationFile,
      beneficiaryBankStatementFile,
      policeReportFormFile,
      dha1680File,
      marriageCertificateFile,
      letterOfAuthorityFile,
      affidavitFile,
      applicationFormFile,
      documentationCompleteDate
    } = data;

    // Set FileDTO in upload config
    this.setFileDataInUploadConfig('funeralClaimFormFileConfig', funeralClaimFormFile);
    this.setFileDataInUploadConfig('dhaBi1663FormFileConfig', dhaBi1663FormFile);
    this.setFileDataInUploadConfig('certifiedCopyOfDeathCertificateFileConfig', certifiedCopyOfDeathCertificateFile);
    this.setFileDataInUploadConfig('deceasedFileConfig', deceasedFile);
    this.setFileDataInUploadConfig('policyHolderIdentificationConfig', policyHolderIdentificationFile);
    this.setFileDataInUploadConfig('beneficiaryIdentificationConfig', beneficiaryIdentificationFile);
    this.setFileDataInUploadConfig('beneficiaryBankStatementConfig', beneficiaryBankStatementFile);
    this.setFileDataInUploadConfig('policeReportFormConfig', policeReportFormFile);
    this.setFileDataInUploadConfig('dha1680FileConfig', dha1680File);
    this.setFileDataInUploadConfig('marriageCertificateConfig', marriageCertificateFile);
    this.setFileDataInUploadConfig('letterOfAuthorityConfig', letterOfAuthorityFile);
    this.setFileDataInUploadConfig('affidavitFileConfig', affidavitFile);
    this.setFileDataInUploadConfig('applicationFormFileConfig', applicationFormFile);

    // Set FileId in FormControls
    this.funeralClaimFormFileFormControl.setValue(funeralClaimFormFile?.fileId);
    this.dhaBi1663FormFileFormControl.setValue(dhaBi1663FormFile?.fileId);
    this.certifiedCopyOfDeathCertificateFileFormControl.setValue(certifiedCopyOfDeathCertificateFile?.fileId);
    this.deceasedFileFormControl.setValue(deceasedFile?.fileId);
    this.policyHolderIdentificationFileFormControl.setValue(policyHolderIdentificationFile?.fileId);
    this.beneficiaryIdentificationFileFormControl.setValue(beneficiaryIdentificationFile?.fileId);
    this.beneficiaryBankStatementFileFileFormControl.setValue(beneficiaryBankStatementFile?.fileId);
    this.policeReportFormFileFormControl.setValue(policeReportFormFile?.fileId);
    this.dha1680FileFormControl.setValue(dha1680File?.fileId);
    this.marriageCertificateFileFormControl.setValue(marriageCertificateFile?.fileId);
    this.letterOfAuthorityFileFormControl.setValue(letterOfAuthorityFile?.fileId);
    this.affidavitFileFileFormControl.setValue(affidavitFile?.fileId);
    this.applicationFormFileFormControl.setValue(applicationFormFile?.fileId);

    if (documentationCompleteDate) {
      this.documentationCompleteDateFormControl.setValue(new Date(documentationCompleteDate));
    }
    this.isDocumentationCompletionDateRequired();
  }

  private setFileDataInUploadConfig(configName: string, fileData: any): void {
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);
    if (config && (fileData && fileData.fileId)) {
      config.uploadedFileData = fileData;
    }
    else {
      config.uploadedFileData = null;
    }
  }

  private sortByColumn(list, columnName: string) {
    return list.sort((firstElement, secondElement) => {
      return new Date(secondElement[columnName]).getTime() - new Date(firstElement[columnName]).getTime();
    });
  }

  private getCaseFuneralDTOPayload(): CaseFuneralDTO {
    let payload: CaseFuneralDTO;
    const caseStatues = this.statusPayload.caseStatuses;
    const caseManagers = this.statusPayload.caseManagers;
    payload = {
      caseFuneralId: this.isEditMode ? this.caseFuneralDTO.caseFuneralId : null,
      dateOfDeath: this.dateOfDeathFormControl.value,
      traditionalLeaderName: this.traditionalLeaderNameFormControl.value,
      traditionalLeaderContactNumber: this.traditionalLeaderContactNumberFormControl.value,
      dhaBarCode: this.dhaBarCodeFormControl.value,
      documentationCompleteDate: this.documentationCompleteDateFormControl.value,
      deathLocation: this.selectedLocationOfDeath,
      deathCertificateDate: this.deathCertificateDateFormControl.value,
      deathCertifier: this.deathCertifierFormControl.value,
      funeralParlour: this.selectedFuneralParlourProvider,
      repatriationProvider: this.selectedRepatriationProviderProvider,
      medicalPractitioner: this.selectedMedicalPractitionerProvider,
      hospital: this.selectedHospitalProvider,
      funeralClaimFormFile: this.getFileDTOPayload('funeralClaimFormFileConfig'),
      dhaBi1663FormFile: this.getFileDTOPayload('dhaBi1663FormFileConfig'),
      certifiedCopyOfDeathCertificateFile: this.getFileDTOPayload('certifiedCopyOfDeathCertificateFileConfig'),
      deceasedFile: this.getFileDTOPayload('deceasedFileConfig'),
      policyHolderIdentificationFile: this.getFileDTOPayload('policyHolderIdentificationConfig'),
      beneficiaryIdentificationFile: this.getFileDTOPayload('beneficiaryIdentificationConfig'),
      beneficiaryBankStatementFile: this.getFileDTOPayload('beneficiaryBankStatementConfig'),
      policeReportFormFile: this.getFileDTOPayload('policeReportFormConfig'),
      dha1680File: this.getFileDTOPayload('dha1680FileConfig'),
      marriageCertificateFile: this.getFileDTOPayload('marriageCertificateConfig'),
      letterOfAuthorityFile: this.getFileDTOPayload('letterOfAuthorityConfig'),
      affidavitFile: this.getFileDTOPayload('affidavitFileConfig'),
      applicationFormFile: this.getFileDTOPayload('applicationFormFileConfig'),
      caseDTO: {
        ...this.associationPayload.caseDTO,
        communicationNumber: this.communicationNumberFormControl.value,
        insuredPerson: this.associationPayload.insuredPerson,
        policy: this.associationPayload.policy,
        product: this.associationPayload.product,
      },
      caseStatuses: caseStatues,
      caseManagers,
      encounterCases: this.isEditMode ? this.transactionPayload.encounterCases : null, // Step is visible on "Edit Mode" only
      issueTicketCases: this.issueTicketsPayload.issueTicketCases,
      created: this.isEditMode ? this.caseFuneralDTO.created : new Date(),
      createdBy: this.isEditMode ? this.caseFuneralDTO.createdBy : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null
    } as CaseFuneralDTO;

    return payload;
  }

  private getFileDTOPayload(configName: string) {
    const config: FileUploadConfig = this.getFileUploadConfigBy(configName);

    if (config && config.uploadedFileData) {
      const updatedFilDate = config.uploadedFileData;
      if (config.clearFileData) {
        /*
         Note:
         clear base64Data file’s byte array. The file’s byte array was already
         posted to the server. Backend will link "fileId" to posted file + this
         will help to avoid unnecessarily big payload posted to Backend.
        */

        updatedFilDate.file = null;
      }
      return updatedFilDate;
    }
    else {
      return {
        // Note: BE just needs the "fileId" when file is null
        fileId: null
      };
    }
  }

  private getFileUploadConfigBy(configName: string): FileUploadConfig {
    return this.fileUploadConfig.find((config: FileUploadConfig) => {
      return config.name === configName;
    });
  }

  private resetState(): void {
    this.caseFuneralDTO = null;

    // Reset FormGroup/Control values:
    this.resetDetailFormGroup();
    this.resetDocumentationFormGroup();
    this.resetTransactionFormGroup();
    this.resetIssueTicketFormGroup();
    this.resetStatusFormGroup();
  }

}
